import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../data/redux/store';

interface UseGeoLocationModalReturn {
  showGeoLocationModal: boolean;
  setShowGeoLocationModal: (show: boolean) => void;
  triggerLocationCheck: () => void;
  hasCheckedLocationToday: boolean;
  isLocationVerified: boolean;
  lastLocationCheck: Date | null;
  resetDailyModalState: () => void;
}

export const useGeoLocationModal = (): UseGeoLocationModalReturn => {
    const [showGeoLocationModal, setShowGeoLocationModal] = useState(false);
    const [hasCheckedLocationToday, setHasCheckedLocationToday] = useState(false);
    const [isLocationVerified, setIsLocationVerified] = useState(false);
    const [lastLocationCheck, setLastLocationCheck] = useState<Date | null>(null);

    // Get user role from Redux
    const userRole = useSelector((state: RootState) => state.auth.roleName);
    
    // Get location verification status from Redux
    const reduxLocationVerified = useSelector((state: RootState) => state.userLocation.isLocationVerified);

    // Check if location verification is required for this role
    const isLocationVerificationRequired = useCallback(() => {
        // Only require location verification for teachers and headmasters
        return userRole === 'teacher' || userRole === 'hm';
    }, [userRole]);

    // Check if location was already verified today
    const checkLocationVerificationStatus = useCallback(() => {
        const today = new Date().toDateString();
        const lastCheck = localStorage.getItem('lastLocationCheck');
        const locationVerified = localStorage.getItem('locationVerified');
        const hasShownToday = localStorage.getItem('hasShownModalToday');
        
        if (lastCheck === today && locationVerified === 'true') {
            setHasCheckedLocationToday(true);
            setIsLocationVerified(true);
            setLastLocationCheck(new Date(lastCheck));
            return true;
        }
        
        // Check if modal was already shown today
        if (hasShownToday === today) {
            setHasCheckedLocationToday(true);
            setIsLocationVerified(locationVerified === 'true');
            setLastLocationCheck(lastCheck ? new Date(lastCheck) : null);
            return true;
        }
        
        return false;
    }, []);

    // Trigger location check (manual trigger)
    const triggerLocationCheck = useCallback(() => {
        if (isLocationVerificationRequired()) {
            console.log('📍 Manual location check triggered');
            setShowGeoLocationModal(true);
        }
    }, [isLocationVerificationRequired]);

    // Check location status on component mount
    useEffect(() => {
        checkLocationVerificationStatus();
    }, [checkLocationVerificationStatus]);

    // Auto-trigger location check only on first login of the day
    useEffect(() => {
        if (isLocationVerificationRequired() && !hasCheckedLocationToday) {
            // Check if modal was already shown today
            const today = new Date().toDateString();
            const hasShownToday = localStorage.getItem('hasShownModalToday');
            
            if (hasShownToday !== today) {
                console.log('🚀 Auto-triggering location check on first login');
                // Small delay to ensure dashboard is fully loaded
                const timer = setTimeout(() => {
                    setShowGeoLocationModal(true);
                    // Mark that modal was shown today
                    localStorage.setItem('hasShownModalToday', today);
                }, 1500); // Increased delay to ensure school data is loaded
                
                return () => clearTimeout(timer);
            }
        }
    }, [isLocationVerificationRequired, hasCheckedLocationToday]);

    // Sync with Redux state
    useEffect(() => {
        if (reduxLocationVerified !== null) {
            setIsLocationVerified(reduxLocationVerified);
        }
    }, [reduxLocationVerified]);

    // Reset daily modal state (useful for testing)
    const resetDailyModalState = useCallback(() => {
        localStorage.removeItem('hasShownModalToday');
        setHasCheckedLocationToday(false);
        setShowGeoLocationModal(false);
        
        // Dispatch custom event to update sidebar
        window.dispatchEvent(new Event('locationVerificationChanged'));
    }, []);

    return {
        showGeoLocationModal,
        setShowGeoLocationModal,
        triggerLocationCheck,
        hasCheckedLocationToday,
        isLocationVerified,
        lastLocationCheck,
        resetDailyModalState
    };
}; 